import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import config from '../../config';
import dayjs from 'dayjs';

import '../../styles/popup.css';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';

import { green } from '@mui/material/colors';
import { inputBaseClasses } from '@mui/material/InputBase';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const AddUnitForm = ({ propertyId, onNext, setSubmitHandler }) => {
    const [formData, setFormData] = useState({
        unitNumber: '',
        property: propertyId || '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        parking: '',
        lotArea: '',
        floorArea: '',
        description: '',
        availableFrom: null, // Initialize to null because of DatePicker
        isAvailable: true,
    });
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: name === 'isAvailable' ? e.target.checked : value,
        }));
    };

    const formDataRef = useRef(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const handleSubmit = useCallback(async () => {
        setError('');

        try {
            const response = await fetch(`${config.API_URL}/v2/unit`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(formDataRef.current),
            });

            const responseData = await response.json();

            if (response.status === 409) {
                setError(responseData.error || 'Duplicate unit found.');
                return false;
            } else if (!response.ok) {
                toast.error('Failed to create unit. Please try again.');
                setError(responseData.error || 'Error creating unit.');
                return false;
            } else if (!responseData._id) {
                toast.error("Invalid unit data.");
                setError("Please fill in all required fields.");
                return false;
            } else {
                toast.success('Unit created successfully!');
                onNext(responseData._id);
                return true;
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('An unexpected error occurred.');
            return false;
        }
    }, [onNext]);

    useEffect(() => {
        if (setSubmitHandler) setSubmitHandler(() => handleSubmit);
    }, [setSubmitHandler, handleSubmit]);

    return (
        <>
            <div className="popup-columns">
                <TextField
                    label="Unit Number"
                    name="unitNumber"
                    variant="outlined"
                    fullWidth
                    value={formData.unitNumber}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <FormControl fullWidth sx={{ marginBottom: '1rem' }}>
                    <InputLabel htmlFor="outlined-adornment-price">Price</InputLabel>
                    <OutlinedInput
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        startAdornment={<InputAdornment position="start">PHP</InputAdornment>}
                        label="Price"
                    />
                </FormControl>
            </div>
            <div className="popup-columns">
                <TextField
                    label="Bedrooms"
                    name="bedrooms"
                    variant="outlined"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
                <TextField
                    label="Bathrooms"
                    name="bathrooms"
                    variant="outlined"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
            </div>
            <div className="popup-columns"><TextField
                label="Lot Area"
                name="lotArea"
                variant="outlined"
                value={formData.lotArea}
                onChange={handleChange}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                        opacity: 1,
                                    },
                                }}
                            >
                                m²
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ width: '100%', marginBottom: '1rem' }}
            />
                <TextField
                    label="Floor Area"
                    name="floorArea"
                    variant="outlined"
                    value={formData.floorArea}
                    onChange={handleChange}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        [`[data-shrink=true] ~ .${inputBaseClasses.root} > &`]: {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    m²
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
                <TextField
                    label="Parking Spaces"
                    name="parking"
                    variant="outlined"
                    fullWidth
                    value={formData.parking}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={handleChange}
                    sx={{ width: '100%', marginBottom: '1rem' }}
                />
            </div>
            <TextField
                label="Description"
                name="description"
                multiline
                fullWidth
                rows={3}
                value={formData.description}
                onChange={handleChange}
                sx={{ marginBottom: '1rem' }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Tooltip title={!formData.isAvailable ? "This can only be edited if the unit is available" : ""} arrow>
                    <span>
                        <DatePicker
                            label="Available From"
                            value={formData.availableFrom ? dayjs(formData.availableFrom) : null}
                            onChange={(newValue) => {
                                const isValid = dayjs(newValue).isValid();
                                setFormData((prevData) => ({
                                    ...prevData,
                                    availableFrom: isValid ? dayjs(newValue).toISOString() : '',
                                }));
                            }}
                            disabled={!formData.isAvailable}
                            sx={{ width: '100%', marginBottom: '1rem' }}
                        />
                    </span>
                </Tooltip>
            </LocalizationProvider>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formData.isAvailable}
                        onChange={(e) =>
                            setFormData((prevData) => ({
                                ...prevData,
                                isAvailable: e.target.checked, // Update state correctly
                            }))
                        }
                        sx={{
                            '&.Mui-checked': {
                                color: green[600],
                            },
                        }}
                    />
                }
                label="Available"
                sx={{
                    fontSize: '14px',
                    '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                    },
                }}
            />
            {error && <p className="error-text w-full font-poppins text-center text-[#FF0000]">{error}</p>}
        </>
    );
};

export default AddUnitForm;
